import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Video,
  Plus,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Settings,
  CalendarClock,
  FileText,
  PlayCircle,
  Users,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "react-hot-toast";

const STATUS_THEME = {
  scheduled:        { bg: "bg-teal-50",  text: "text-teal-700",  accent: "text-teal-500", badge: "bg-teal-100 text-teal-700 border-teal-200", label: "Scheduled" },
  completed:        { bg: "bg-green-50", text: "text-green-700", accent: "text-green-500", badge: "bg-green-100 text-green-700 border-green-200", label: "Completed" },
  cancelled:        { bg: "bg-slate-100",text: "text-slate-500", accent: "text-slate-400", badge: "bg-red-100 text-red-600 border-red-200", label: "Cancelled" },
  pending_approval: { bg: "bg-amber-50", text: "text-amber-700", accent: "text-amber-500", badge: "bg-amber-100 text-amber-700 border-amber-200", label: "Pending" },
};

function MeetingCard({ meeting, user, onClick }) {
  const theme = STATUS_THEME[meeting.status] || STATUS_THEME.scheduled;
  const isTeacher = user?.role === "teacher";
  const isGroup = meeting.meeting_type === "group";
  const otherName = isGroup
    ? `${(meeting.participants || []).filter(p => p.status === "attending").length || "All"} students`
    : isTeacher ? meeting.student_name : meeting.teacher_name;
  const dt = meeting.scheduled_at ? parseISO(meeting.scheduled_at) : null;
  const isCancelled = meeting.status === "cancelled";

  return (
    <div
      data-testid={`meeting-card-${meeting.meeting_id}`}
      className={`group bg-white border rounded-2xl overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 ${isCancelled ? "opacity-60" : ""} ${isGroup ? "border-indigo-200 hover:border-indigo-400 ring-1 ring-indigo-100" : "border-slate-200 hover:border-teal-300"}`}
      onClick={onClick}
    >
      {/* Group class top banner */}
      {isGroup && (
        <div className="bg-indigo-600 px-4 py-1.5 flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 text-indigo-200" />
          <span className="text-[11px] font-bold text-white uppercase tracking-wider">Group Class</span>
        </div>
      )}
      <div className="flex h-full">
        {/* Calendar date block */}
        {dt && (
          <div className={`w-20 shrink-0 flex flex-col items-center justify-center text-center ${isGroup ? "bg-indigo-50" : theme.bg}`}>
            <span className={`text-[11px] font-bold uppercase tracking-wider ${isGroup ? "text-indigo-400" : theme.accent}`}>
              {format(dt, "EEE")}
            </span>
            <span className={`text-3xl font-extrabold leading-none mt-0.5 ${isGroup ? "text-indigo-700" : theme.text}`}>
              {format(dt, "d")}
            </span>
            <span className={`text-[11px] font-semibold ${isGroup ? "text-indigo-400" : theme.accent}`}>
              {format(dt, "MMM")}
            </span>
          </div>
        )}
        {/* Meeting info */}
        <div className="flex-1 p-4 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-slate-900 truncate">{meeting.title}</p>
            <Badge variant="outline" className={`shrink-0 text-[10px] h-5 ${theme.badge}`}>
              {theme.label}
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 truncate">
            {isGroup ? <><Users className="w-3 h-3 inline mr-0.5 -mt-0.5" />{otherName}</> : `with ${otherName || "Unknown"}`}
          </p>
          {dt && (
            <p className="text-lg font-bold text-slate-800 mt-2 leading-tight">
              {format(dt, "h:mm a")}
            </p>
          )}
          <div className="flex items-center gap-3 mt-1.5">
            {meeting.duration_minutes && (
              <span className="text-[11px] text-slate-400">{meeting.duration_minutes} min</span>
            )}
            {meeting.zoom_link && (
              <span className="flex items-center gap-0.5 text-[11px] text-blue-500">
                <Video className="w-3 h-3" /> Zoom
              </span>
            )}
            {meeting.notes && (
              <span className="flex items-center gap-0.5 text-[11px] text-green-500">
                <FileText className="w-3 h-3" /> Notes
              </span>
            )}
            {meeting.recording_url && (
              <span className="flex items-center gap-0.5 text-[11px] text-purple-500">
                <PlayCircle className="w-3 h-3" /> Recording
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MeetingGrid({ meetings, user, navigate, emptyIcon: EmptyIcon, emptyTitle, emptyDesc, loading }) {
  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-teal-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (meetings.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
          <EmptyIcon className="w-7 h-7 text-slate-400" />
        </div>
        <h3 className="font-semibold text-slate-700">{emptyTitle}</h3>
        <p className="text-sm text-slate-400 mt-1">{emptyDesc}</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {meetings.map((m) => (
        <MeetingCard
          key={m.meeting_id}
          meeting={m}
          user={user}
          onClick={() => navigate(`/meetings/${m.meeting_id}`)}
        />
      ))}
    </div>
  );
}

export default function Meetings({ user }) {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [studentFilter, setStudentFilter] = useState("all");
  const [form, setForm] = useState({
    title: "",
    description: "",
    student_id: "",
    scheduled_at: "",
    duration_minutes: "15",
    zoom_link: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [groupForm, setGroupForm] = useState({
    title: "",
    description: "",
    scheduled_at: "",
    duration_minutes: "15",
    zoom_link: "",
  });

  const isTeacher = user?.role === "teacher";

  useEffect(() => {
    fetchMeetings();
    if (isTeacher) {
      fetchStudents();
    } else {
      fetchTeachers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacher]);

  const fetchMeetings = async () => {
    try {
      const res = await axios.get(`${API}/meetings`);
      setMeetings(res.data);
    } catch (err) {
      console.error("Error fetching meetings:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const res = await axios.get(`${API}/users/students`);
      setStudents(res.data);
    } catch (err) {
      console.error("Failed to fetch students:", err);
    }
  };

  const fetchTeachers = async () => {
    try {
      const res = await axios.get(`${API}/users/teachers`);
      setTeachers(res.data);
    } catch (err) {
      console.error("Failed to fetch teachers:", err);
    }
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.scheduled_at) {
      toast.error("Title and date/time are required");
      return;
    }
    if (isTeacher && !form.student_id) {
      toast.error("Please select a student");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/meetings`, {
        title: form.title.trim(),
        description: form.description.trim() || null,
        student_id: isTeacher ? form.student_id : user.user_id,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_minutes: parseInt(form.duration_minutes) || 15,
        zoom_link: form.zoom_link.trim() || null,
      });
      toast.success(isTeacher ? "Meeting scheduled" : "Meeting request sent");
      setDialogOpen(false);
      setForm({ title: "", description: "", student_id: "", scheduled_at: "", duration_minutes: "15", zoom_link: "" });
      fetchMeetings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create meeting");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupForm.title.trim() || !groupForm.scheduled_at) {
      toast.error("Title and date/time are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/meetings/group`, {
        title: groupForm.title.trim(),
        description: groupForm.description.trim() || null,
        scheduled_at: new Date(groupForm.scheduled_at).toISOString(),
        duration_minutes: parseInt(groupForm.duration_minutes) || 15,
        zoom_link: groupForm.zoom_link.trim() || null,
      });
      toast.success("Group class created — all students notified");
      setGroupDialogOpen(false);
      setGroupForm({ title: "", description: "", scheduled_at: "", duration_minutes: "15", zoom_link: "" });
      fetchMeetings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create group class");
    } finally {
      setSubmitting(false);
    }
  };

  const now = new Date();
  const upcomingMeetings = meetings.filter(
    (m) => (m.status === "scheduled" || m.status === "pending_approval") && new Date(m.scheduled_at) >= now
  );
  const pendingMeetings = meetings.filter((m) => m.status === "pending_approval");
  const pastMeetings = meetings.filter(
    (m) => m.status === "completed" || m.status === "cancelled" || (m.status === "scheduled" && new Date(m.scheduled_at) < now)
  );

  const sortedUpcoming = [...upcomingMeetings].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const sortedPast = [...pastMeetings].sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
  const filteredUpcoming = studentFilter === "all" ? sortedUpcoming : sortedUpcoming.filter((m) => m.student_id === studentFilter);
  const filteredPast = studentFilter === "all" ? sortedPast : sortedPast.filter((m) => m.student_id === studentFilter);

  const uniqueStudents = [...new Map(
    meetings.map((m) => [m.student_id, { id: m.student_id, name: m.student_name }])
  ).values()];

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="meetings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-bold text-slate-900 tracking-tight">
              Meetings
            </h1>
            <p className="text-slate-500 mt-1">
              {isTeacher ? "Schedule and manage student meetings" : "View and request meetings with your teacher"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isTeacher ? (
              <>
                <Button
                  data-testid="availability-settings-btn"
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => navigate("/availability-settings")}
                >
                  <Settings className="w-4 h-4 mr-1.5" />
                  Availability
                </Button>
                <Button
                  data-testid="create-group-class-btn"
                  size="sm"
                  variant="outline"
                  className="rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  onClick={() => setGroupDialogOpen(true)}
                >
                  <Users className="w-4 h-4 mr-1.5" />
                  Group Class
                </Button>
                <Button
                  data-testid="schedule-meeting-btn"
                  size="sm"
                  onClick={() => setDialogOpen(true)}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-full shadow-md shadow-teal-600/20"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Individual
                </Button>
              </>
            ) : teachers.length > 0 ? (
              <Button
                data-testid="book-meeting-btn"
                size="sm"
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-full shadow-md shadow-teal-600/20"
                onClick={() => navigate(`/book-meeting/${teachers[0].user_id}`)}
              >
                <CalendarClock className="w-4 h-4 mr-1.5" />
                Book a Meeting
              </Button>
            ) : null}
          </div>
        </div>

        {/* Filters (teacher only) */}
        {isTeacher && uniqueStudents.length > 0 && (
          <div className="flex items-center gap-3">
            <Label className="text-xs uppercase tracking-widest text-slate-400 font-bold">Filter</Label>
            <Select value={studentFilter} onValueChange={setStudentFilter}>
              <SelectTrigger data-testid="student-filter-select" className="w-48 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Students</SelectItem>
                {uniqueStudents.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 rounded-full p-1">
            <TabsTrigger data-testid="tab-upcoming" value="upcoming" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Upcoming
              {filteredUpcoming.length > 0 && (
                <Badge className="ml-2 h-5 min-w-5 bg-teal-500 hover:bg-teal-500 text-white text-xs">
                  {filteredUpcoming.length}
                </Badge>
              )}
            </TabsTrigger>
            {isTeacher && (
              <TabsTrigger data-testid="tab-pending" value="pending" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Pending
                {pendingMeetings.length > 0 && (
                  <Badge className="ml-2 h-5 min-w-5 bg-amber-500 hover:bg-amber-500 text-white text-xs">
                    {pendingMeetings.length}
                  </Badge>
                )}
              </TabsTrigger>
            )}
            <TabsTrigger data-testid="tab-past" value="past" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Past
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="mt-6">
            <MeetingGrid
              meetings={filteredUpcoming}
              user={user}
              navigate={navigate}
              loading={loading}
              emptyIcon={Calendar}
              emptyTitle="No upcoming meetings"
              emptyDesc={isTeacher ? "Schedule a meeting with a student to get started." : "No meetings scheduled yet."}
            />
          </TabsContent>

          {isTeacher && (
            <TabsContent value="pending" className="mt-6">
              <MeetingGrid
                meetings={pendingMeetings}
                user={user}
                navigate={navigate}
                loading={false}
                emptyIcon={AlertCircle}
                emptyTitle="No pending requests"
                emptyDesc="Student meeting requests will appear here."
              />
            </TabsContent>
          )}

          <TabsContent value="past" className="mt-6">
            <MeetingGrid
              meetings={filteredPast}
              user={user}
              navigate={navigate}
              loading={false}
              emptyIcon={Clock}
              emptyTitle="No past meetings"
              emptyDesc="Completed and cancelled meetings will appear here."
            />
          </TabsContent>
        </Tabs>

        {/* Schedule Meeting Dialog (teacher only) */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Schedule a Meeting</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Title *</Label>
                <Input
                  data-testid="meeting-title-input"
                  placeholder="e.g., Weekly discipleship check-in"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Description / Agenda</Label>
                <Textarea
                  data-testid="meeting-description-input"
                  placeholder="What will be discussed..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                />
              </div>
              {isTeacher && (
                <div>
                  <Label>Student *</Label>
                  <Select value={form.student_id} onValueChange={(val) => setForm({ ...form, student_id: val })}>
                    <SelectTrigger data-testid="meeting-student-select">
                      <SelectValue placeholder="Select a student" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((s) => (
                        <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date & Time *</Label>
                  <Input
                    data-testid="meeting-datetime-input"
                    type="datetime-local"
                    value={form.scheduled_at}
                    onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Duration</Label>
                  <Select value={form.duration_minutes} onValueChange={(val) => setForm({ ...form, duration_minutes: val })}>
                    <SelectTrigger data-testid="meeting-duration-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Zoom Meeting Link</Label>
                <Input
                  data-testid="meeting-zoom-input"
                  placeholder="https://zoom.us/j/..."
                  value={form.zoom_link}
                  onChange={(e) => setForm({ ...form, zoom_link: e.target.value })}
                />
                <p className="text-xs text-slate-400 mt-1">Optional — can be added later</p>
              </div>
              <Button
                data-testid="meeting-submit-btn"
                className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                onClick={handleCreate}
                disabled={submitting}
              >
                {submitting ? "Creating..." : "Schedule Meeting"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Group Class Dialog */}
        <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 text-indigo-600" />
                </div>
                <DialogTitle className="font-serif text-xl">Schedule Group Class</DialogTitle>
              </div>
              <p className="text-xs text-slate-500 mt-1">All students will be automatically added and notified.</p>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Class Title *</Label>
                <Input
                  data-testid="group-title-input"
                  placeholder="e.g., Sunday Bible Study, Prayer Meeting..."
                  value={groupForm.title}
                  onChange={(e) => setGroupForm({ ...groupForm, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Description / Agenda</Label>
                <Textarea
                  data-testid="group-description-input"
                  placeholder="What will be covered..."
                  value={groupForm.description}
                  onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date & Time *</Label>
                  <Input
                    data-testid="group-datetime-input"
                    type="datetime-local"
                    value={groupForm.scheduled_at}
                    onChange={(e) => setGroupForm({ ...groupForm, scheduled_at: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Duration</Label>
                  <Select value={groupForm.duration_minutes} onValueChange={(val) => setGroupForm({ ...groupForm, duration_minutes: val })}>
                    <SelectTrigger data-testid="group-duration-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 min</SelectItem>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="45">45 min</SelectItem>
                      <SelectItem value="60">60 min</SelectItem>
                      <SelectItem value="90">90 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Zoom Meeting Link</Label>
                <Input
                  data-testid="group-zoom-input"
                  placeholder="https://zoom.us/j/..."
                  value={groupForm.zoom_link}
                  onChange={(e) => setGroupForm({ ...groupForm, zoom_link: e.target.value })}
                />
              </div>
              <Button
                data-testid="group-submit-btn"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
                onClick={handleCreateGroup}
                disabled={submitting}
              >
                {submitting ? "Creating..." : "Create Group Class"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
